import React, { useEffect, useRef, useState } from "react";
import DropdownList from "./DropdownList";
import "./dropdown.css";
import Input from "../../FormComponents/Input";

const Dropdown = ({
  options,
  selected,
  onSelect,
  onCreate = () => {},
  onDelete = () => {},
  placeholder = "Select...",
  isDisabled = false
}) => {
  const [inputValue, setInputValue] = useState(selected?.name || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isLoading, setIsLoading] = useState(false); // New state for loading
  const inputRef = useRef();
  useEffect(() => {
    setFilteredOptions(options.filter((opt) => opt.name.toLowerCase().includes(inputValue.toLowerCase())));
  }, [inputValue, options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [highlightedIndex]);

  const optionRefs = useRef([]);
  optionRefs.current = [];

  const handleChange = (e) => {
    setInputValue(e.target.value);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = async (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? -1 : prev - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        const selected = filteredOptions[highlightedIndex];
        onSelect(selected);
        setInputValue(selected.name);
      } else if (trimmed) {
        // Check for duplicate by name (case-insensitive)
        const exists = options.some((opt) => opt.name.toLowerCase() === trimmed.toLowerCase());

        if (exists) {
          // Optional: give user feedback if needed
          setInputValue(trimmed); // Still set the input value
        } else {
          setIsLoading(true); // Start loading spinner
          const newOption = await onCreate(trimmed);
          setIsLoading(false); // Stop loading spinner after creation
          onSelect(newOption);
          setInputValue(newOption.name);
        }
      }

      setShowDropdown(false);
      // inputRef.current.blur();
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      inputRef.current.blur();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const trimmed = inputValue.trim().toLowerCase();
      const matchedOption = options.find((opt) => opt.name.toLowerCase() === trimmed);

      if (matchedOption) {
        if (!selected || selected.id !== matchedOption.id) {
          onSelect(matchedOption);
        }
      } else {
        setInputValue("");
      }

      setShowDropdown(false);
    }, 100);
  };

  const handleFocus = () => {
    if (!isDisabled) setShowDropdown(true);
  };

  const trimmedInput = inputValue.trim().toLowerCase();
  const isValidInput = options.some((opt) => opt.name.toLowerCase() === trimmedInput);
  const showStatusDot = trimmedInput.length > 0;

  return (
    <div className="dropdown-wrapper">
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={isDisabled || isLoading}
      />

      {/* Show spinner while loading */}
      {isLoading && <div className="spinner"></div>}
      {showStatusDot && !isLoading && (
        <div
          className={`input-status-dot ${isValidInput ? "valid" : "invalid"}`}
          title={isValidInput ? "Exists" : "Not in list"}
        />
      )}

      {showDropdown && !isLoading && (
        <DropdownList
          options={filteredOptions}
          onOptionClick={(opt) => {
            onSelect(opt);
            setInputValue(opt.name);
            setShowDropdown(false);
          }}
          highlightedIndex={highlightedIndex}
          optionRefs={optionRefs}
          onDelete={onDelete} // Pass onDelete function to DropdownList
        />
      )}
    </div>
  );
};

export default Dropdown;
